import { z } from 'zod';
import { hasAtMostTwoDecimals } from '../money';

/**
 * Upper bound for a single synagogue transaction. Far above anything real
 * (the largest genuine entry on record is ₪95,863.66), low enough to stop a
 * runaway typo like ₪109,815,715 at the door.
 */
export const MAX_TRANSACTION_AMOUNT = 10_000_000;

/** A synagogue amount: > 0, at most 2 decimals (agorot). */
export const synagogueAmountSchema = z
  .number({ invalid_type_error: 'סכום חייב להיות מספר' })
  .positive({ message: 'סכום חייב להיות גדול מ-0' })
  .max(MAX_TRANSACTION_AMOUNT, { message: 'סכום גבוה מדי — בדקו שוב את הסכום' })
  .refine(hasAtMostTwoDecimals, { message: 'עד 2 ספרות אחרי הנקודה (אגורות)' });

/** Body of PUT /synagogue/transactions/:id — correcting a mistyped amount. */
export const transactionAmountCorrectionSchema = z.object({
  amountPaid: synagogueAmountSchema,
});

// Synagogue transaction. actionType is a strict enum:
//   1 = income, 2 = expense.
export const transactionSchema = z.object({
  actionType: z.union(
    [z.literal(1), z.literal(2)],
    { errorMap: () => ({ message: 'בחר סוג פעולה (הכנסה/הוצאה)' }) },
  ),
  paymentType: z
    .string()
    .min(1, { message: 'הזן סוג תשלום' })
    .max(20, { message: 'סוג תשלום ארוך מדי' }),
  expenseType: z.string().optional(),
  checkNo: z.string().optional(),
  amountPaid: synagogueAmountSchema,
  paidTo: z
    .string()
    .min(1, { message: 'הזן שם' })
    .max(50, { message: 'שם ארוך מדי' }),
  // createdAt is set on the server at insert time, not by the client.
  // (Previously this defaulted to new Date() at module load — wrong.)
});

export type ITransaction = z.infer<typeof transactionSchema>;

export type TransactionType = {
  _id: string;
  actionType: 1 | 2;
  paymentType: string;
  expenseType?: string;
  checkNo?: string;
  amountPaid: number;
  paidTo: string;
  createdAt: Date;
};
